import type { SignInStep, MfaStep } from './signIn.types';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getEnvConfig } from '@configs/env';
import { useGoogleLogin } from '@react-oauth/google';
import { AxiosError } from 'axios';
import qs from 'qs';
import authStore from '@stores/AuthStore';
import { useStore } from '@stores/RootStore';
import localStorage from '@repositories/browserStorage/localStorage/LocalStorage';
import sessionStorage from '@repositories/browserStorage/sessionStorage/SessionStorage';
import type {
  TenantSsoProviderPublicDTO,
  AuthDiscoverResultDTO,
} from '@repositories/tenantSsoProviderRepository/tenantSsoProvider.types';
import type { User } from '@repositories/userRepository/user.types';
import { useOAuthCallback, useSsoCallback } from '@customHooks/useOAuthCallback';
import { useSsoRedirect } from '@customHooks/useSsoRedirect';
import { useVerifyMfa } from '@queryHooks/useMfa';
import { useOAuthSignIn } from '@queryHooks/useOauth';
import { useGetMyTenantList } from '@queryHooks/useTenant';
import { useDiscoverLoginMethod } from '@queryHooks/useTenantSsoProvider';
import { useSignIn, useExchangeToken } from '@queryHooks/useUser';
import {
  getValidRouteAfterLogin,
  clearRouteAfterLogin,
  saveRouteAfterLogin,
  type RouteAfterLogin,
} from '@utils/routeStorage';
import { getErrorMessage } from '@locales/i18nErrorMessage';

type UseSignInFlowParams = {
  isItAdminPage?: boolean;
};

export const useSignInFlow = ({ isItAdminPage }: UseSignInFlowParams) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId } = useParams();
  const {
    uiStore: { alertStore },
  } = useStore();

  // 환경변수 파싱
  const { MICROSOFT_CLIENT_ID, GOOGLE_CLIENT_ID, PASS_TENANT_SELECT_PAGE, ENABLED_USER_LOGIN_CASE, DISABLE_PASSWORD_LOGIN, SHOW_SSO_AS_DEFAULT } = getEnvConfig();
  // VITE_ENABLED_USER_LOGIN_CASE는 OAuth 버튼(Google/Microsoft) 표시 여부만 제어
  const enabledOAuthMethods = ENABLED_USER_LOGIN_CASE
    ? ENABLED_USER_LOGIN_CASE.split('_').filter(Boolean)
    : ['MICROSOFT', 'GOOGLE'];

  const showGoogleLogin = enabledOAuthMethods.includes('GOOGLE') && !!GOOGLE_CLIENT_ID;
  const showMicrosoftLogin = enabledOAuthMethods.includes('MICROSOFT') && !!MICROSOFT_CLIENT_ID;
  // 비밀번호 로그인 비활성화 환경 (KTDS 등): SSO만 노출
  const disablePasswordLogin = DISABLE_PASSWORD_LOGIN === 'true';
  // 최초 화면에 SSO 버튼만 노출 (이메일 입력 없이 바로 SSO 버튼 표시)
  const showSsoAsDefault = SHOW_SSO_AS_DEFAULT === 'true';

  // Microsoft OAuth 리다이렉트 후 sessionStorage에서 저장된 상태 복원
  const getMicrosoftCallbackSession = () => {
    const rawOauthProvider = sessionStorage.get('oauth_provider');
    if (rawOauthProvider !== 'microsoft') return null;

    const rawEmail = sessionStorage.get('sso_login_email');
    const rawRemember = sessionStorage.get('sso_remember_email');
    return {
      email: typeof rawEmail === 'string' ? rawEmail : '',
      rememberEmail: typeof rawRemember === 'string' ? rawRemember === 'true' : false,
    };
  };

  // 아이디 기억하기 (OAuth 리다이렉트 후 복원 우선, 없으면 localStorage)
  const [initialEmailState] = useState(() => {
    const oauthSession = getMicrosoftCallbackSession();
    if (oauthSession?.email) return oauthSession.email;

    const saved = localStorage.get('rememberedEmail');
    return typeof saved === 'string' ? saved : '';
  });
  const [rememberEmail, setRememberEmail] = useState(() => {
    const oauthSession = getMicrosoftCallbackSession();
    if (oauthSession) return oauthSession.rememberEmail;
    return initialEmailState.length > 0;
  });

  // 상태 머신
  const [step, setStep] = useState<SignInStep>('EMAIL_INPUT');
  const [email, setEmail] = useState(initialEmailState);
  const [ssoProviders, setSsoProviders] = useState<TenantSsoProviderPublicDTO[]>([]);

  // 인증 에러 상태
  const [isAuthenticationFailed, setIsAuthenticationFailed] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState<string | null>(null);
  const [invalidTenant, setInvalidTenant] = useState(false);

  // MFA 상태
  const [mfaStep, setMfaStep] = useState<MfaStep>('LOGIN');
  const [mfaQrCodeUrl, setMfaQrCodeUrl] = useState<string | null>(null);
  const [mfaSecret, setMfaSecret] = useState<string | null>(null);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaLoading, setMfaLoading] = useState(false);

  // API 훅
  const { mutate: login, isPending: isLoginPending } = useSignIn(isItAdminPage);
  const { mutate: verifyMfa } = useVerifyMfa();
  const { mutate: oauthSignIn } = useOAuthSignIn();
  const { handleOAuthCallback } = useOAuthCallback();
  const { handleSsoCallback } = useSsoCallback();
  const { mutate: exchangeToken } = useExchangeToken();
  const { mutate: discoverLoginMethod, isPending: isDiscoverPending } = useDiscoverLoginMethod();
  const isLookupPending = isDiscoverPending;
  const { redirectToSsoProvider, isRedirecting } = useSsoRedirect();

  const { refetch: fetchMyTenants } = useGetMyTenantList(
    {},
    {
      enabled: false,
    },
  );

  // --- 로그인 후처리 ---

  const handleSavedRoute = useCallback(
    (savedRoute: RouteAfterLogin): boolean => {
      try {
        if (savedRoute.tenantId) {
          authStore.updateCurTenant(savedRoute.tenantId);
        }
        authStore.postLogin();
      } catch {
        // 저장된 경로의 tenantId가 현재 AP에 없으면 무시하고 일반 후처리로 진행
        return false;
      }

      const isManagerRoute = savedRoute.path.startsWith('/manager/');
      if (isManagerRoute && !isItAdminPage) {
        window.location.href = savedRoute.path;
      } else {
        navigate(savedRoute.path, { replace: true });
      }
      return true;
    },
    [navigate, isItAdminPage],
  );

  const handleLocationState = useCallback((): boolean => {
    if (!location.state?.routeAfterLogin) {
      return false;
    }

    try {
      if (location.state?.tenantId) {
        authStore.updateCurTenant(location.state.tenantId ?? tenantId);
      }
      authStore.postLogin();
    } catch {
      // location.state의 tenantId가 현재 AP에 없으면 무시하고 일반 후처리로 진행
      return false;
    }
    navigate(location.state.routeAfterLogin, { replace: true });
    return true;
  }, [location.state, navigate, tenantId]);

  // 풀 토큰 상태에서의 후처리 (테넌트 확정 완료)
  const loginPostProcess = useCallback(async () => {
    const savedRoute = getValidRouteAfterLogin();
    if (savedRoute) {
      clearRouteAfterLogin();
      handleSavedRoute(savedRoute);
      return;
    }

    if (handleLocationState()) {
      return;
    }

    // IT Admin 페이지에서 로그인: tenantId가 URL에 있으므로 바로 매니저 홈으로 이동
    if (isItAdminPage && tenantId) {
      authStore.updateCurTenant(tenantId);
      authStore.postLogin();
      navigate(`/manager/${tenantId}/home`, { replace: true });
      return;
    }

    // OAuth 콜백이 /login에서 처리된 경우, sessionStorage에 저장된 매니저 컨텍스트 복원
    const savedManagerTenantId = sessionStorage.get('oauth_manager_tenant_id');
    if (sessionStorage.get('oauth_is_admin_page') === 'true' && typeof savedManagerTenantId === 'string') {
      sessionStorage.removeItem('oauth_is_admin_page');
      sessionStorage.removeItem('oauth_manager_tenant_id');
      authStore.updateCurTenant(savedManagerTenantId);
      authStore.postLogin();
      window.location.href = `/manager/${savedManagerTenantId}/home`;
      return;
    }

    if (PASS_TENANT_SELECT_PAGE === 'true') {
      const { data: myTenants } = await fetchMyTenants();
      const onlyOneTenant = myTenants?.length === 1;
      const firstTenantId = onlyOneTenant ? myTenants?.[0]?.id : null;

      if (onlyOneTenant && firstTenantId) {
        authStore.updateCurTenant(firstTenantId);
        authStore.postLogin();
        window.location.href = `/manager/${firstTenantId}/store`;
      } else {
        navigate('/select-tenant', { replace: true, state: { email: email.trim() } });
      }
      return;
    }

    // 일반 직원 로그인: 테넌트 선택 또는 직원 홈으로 이동
    // authStore.login()에서 curTenant가 이미 설정됨 (테넌트 1개인 경우)
    if (!authStore.curTenant?.id) {
      navigate('/select-tenant', {
        replace: true,
        state: { email: email.trim() },
      });
    } else {
      navigate('/', { replace: true });
    }
  }, [
    PASS_TENANT_SELECT_PAGE,
    email,
    fetchMyTenants,
    handleLocationState,
    handleSavedRoute,
    isItAdminPage,
    navigate,
    tenantId,
  ]);

  // 제한 토큰 상태 → 테넌트 선택 페이지로 이동
  const navigateToSelectTenant = useCallback(() => {
    navigate('/select-tenant', {
      replace: true,
      state: { email: email.trim() },
    });
  }, [email, navigate]);

  // --- 아이디 기억하기 저장 ---

  const saveRememberedEmail = useCallback(() => {
    if (rememberEmail) {
      localStorage.set('rememberedEmail', email.trim());
    } else {
      localStorage.removeItem('rememberedEmail');
    }
  }, [rememberEmail, email]);

  // 인증 성공 후처리: 이메일 저장 + 제한 토큰이면 테넌트 선택, 풀 토큰이면 후처리 진행
  const handleAuthSuccess = useCallback(() => {
    saveRememberedEmail();
    if (authStore.isLimitedToken) {
      // manager 로그인 페이지에서는 tenantId가 URL에 있으므로 바로 토큰 교환
      if (isItAdminPage && tenantId) {
        exchangeToken(
          { grantType: 'token_exchange', tenantId },
          {
            onSuccess: user => {
              const success = authStore.upgradeToFullToken(user.additionalPermissions, tenantId);
              if (!success) {
                navigate('/error', {
                  state: { errorNum: 403, errorText: 'INVALID_TENANT' },
                });
                return;
              }
              navigate(`/manager/${tenantId}/home`, { replace: true });
            },
            onError: () => {
              navigateToSelectTenant();
            },
          },
        );
        return;
      }
      // OAuth 콜백이 /login에서 처리된 경우, sessionStorage에 저장된 매니저 컨텍스트 복원
      const savedManagerTenantId = sessionStorage.get('oauth_manager_tenant_id');
      if (sessionStorage.get('oauth_is_admin_page') === 'true' && typeof savedManagerTenantId === 'string') {
        sessionStorage.removeItem('oauth_is_admin_page');
        sessionStorage.removeItem('oauth_manager_tenant_id');
        exchangeToken(
          { grantType: 'token_exchange', tenantId: savedManagerTenantId },
          {
            onSuccess: user => {
              const success = authStore.upgradeToFullToken(user.additionalPermissions, savedManagerTenantId);
              if (!success) {
                navigate('/error', { state: { errorNum: 403, errorText: 'INVALID_TENANT' } });
                return;
              }
              window.location.href = `/manager/${savedManagerTenantId}/home`;
            },
            onError: () => {
              navigateToSelectTenant();
            },
          },
        );
        return;
      }
      navigateToSelectTenant();
      return;
    }
    loginPostProcess();
  }, [loginPostProcess, navigateToSelectTenant, isItAdminPage, tenantId, exchangeToken, navigate, saveRememberedEmail]);

  // --- 이메일 제출 (auth/discover API) ---

  const handleEmailContinue = () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    discoverLoginMethod(
      { email: trimmedEmail, ...(tenantId && { tenantId }) },
      {
        onSuccess: (result: AuthDiscoverResultDTO) => {
          // NORMAL → 비밀번호 입력
          if (result.loginMethod === 'NORMAL') {
            setStep('PASSWORD_INPUT');
            return;
          }

          // TENANT_SSO → SSO 프로바이더로 리다이렉트/선택
          const providers = result.ssoProviders ?? [];

          if (providers.length === 0) {
            // SSO 프로바이더 정보 없음 → 비밀번호 입력 fallback
            setStep('PASSWORD_INPUT');
            return;
          }

          if (providers.length === 1) {
            setStep('SSO_REDIRECTING');
            redirectToSsoProvider(providers[0], trimmedEmail);
            return;
          }

          // 프로바이더 2개+ → 선택 화면
          setSsoProviders(providers);
          setStep('SSO_PROVIDER_SELECT');
        },
        onError: () => {
          // discover 실패 시 비밀번호 입력으로 fallback
          setStep('PASSWORD_INPUT');
        },
      },
    );
  };

  // --- 비밀번호 로그인 ---

  const handlePasswordLogin = (data: { username: string; password: string }) => {
    login(data, {
      onSuccess: (user: User) => {
        if (user.mfaSetupRequired && user.mfaQrCodeUrl && user.mfaSecret) {
          setMfaQrCodeUrl(user.mfaQrCodeUrl);
          setMfaSecret(user.mfaSecret);
          setMfaStep('MFA_SETUP');
          setStep('MFA_SETUP');
          return;
        }

        if (user.mfaRequired) {
          setMfaStep('MFA_VERIFY');
          setStep('MFA_VERIFY');
          return;
        }

        handleAuthSuccess();
      },
      onError: (err: AxiosError | Error) => {
        if (err instanceof AxiosError) {
          const errorInfo = getErrorMessage(err);
          const errorMessage = errorInfo.errorType ? t(`Error.${errorInfo.errorType}`) : errorInfo.message;
          setLoginErrorMessage(errorMessage);
          setIsAuthenticationFailed(true);
          setInvalidTenant(false);
        } else if (err.message === 'INVALID_TENANT') {
          setIsAuthenticationFailed(false);
          setInvalidTenant(true);
          setLoginErrorMessage(null);
        }
      },
    });
  };

  // --- SSO 프로바이더 선택 ---

  const handleSsoProviderSelect = (provider: TenantSsoProviderPublicDTO) => {
    setStep('SSO_REDIRECTING');
    // 매니저 페이지에서 호출된 경우: SSO 콜백이 /login에 도달해도 매니저 컨텍스트 복원
    // 그 외(employee)에서 호출된 경우: 이전에 저장된 매니저 컨텍스트 제거
    if (isItAdminPage && tenantId) {
      sessionStorage.set('oauth_is_admin_page', 'true');
      sessionStorage.set('oauth_manager_tenant_id', tenantId);
    } else {
      sessionStorage.removeItem('oauth_is_admin_page');
      sessionStorage.removeItem('oauth_manager_tenant_id');
    }
    redirectToSsoProvider(provider, email.trim());
  };

  // --- MFA ---

  const handleMfaVerify = useCallback(
    (otpCode: string) => {
      setMfaLoading(true);
      setMfaError(null);

      verifyMfa(otpCode, {
        onSuccess: data => {
          if (data.verified && data.user) {
            handleAuthSuccess();
          } else {
            const baseError = t('Mfa.verification.error.invalidCode');
            const errorText =
              data.remainingAttempts > 0
                ? `${baseError} ${t('Mfa.verification.remainingAttempts', { count: data.remainingAttempts })}`
                : baseError;
            setMfaError(errorText);
          }
        },
        onError: (error: AxiosError) => {
          const errorInfo = getErrorMessage(error);
          const errorMessage = errorInfo.errorType ? t(`Error.${errorInfo.errorType}`) : errorInfo.message;
          if (error.response?.status === 401) {
            alertStore.open({
              title: t('Mfa.verification.error.title'),
              message: errorMessage,
              useCancel: false,
              onConfirm: () => {
                handleBackToEmail();
              },
            });
          } else {
            setMfaError(errorMessage);
          }
        },
        onSettled: () => {
          setMfaLoading(false);
        },
      });
    },
    [alertStore, handleAuthSuccess, t, verifyMfa],
  );

  const handleChangeMfaStep = (newMfaStep: MfaStep) => {
    setMfaStep(newMfaStep);
  };

  // --- 네비게이션 ---

  const handleBackToEmail = () => {
    setStep('EMAIL_INPUT');
    setMfaStep('LOGIN');
    setMfaQrCodeUrl(null);
    setMfaSecret(null);
    setMfaError(null);
    setIsAuthenticationFailed(false);
    setLoginErrorMessage(null);
    setInvalidTenant(false);
    sessionStorage.removeItem('tk');
    sessionStorage.removeItem('authInfo');
    sessionStorage.removeItem('sso_remember_email');
  };

  // --- Google OAuth (popup 방식) ---

  const googleLogin = useGoogleLogin({
    onSuccess: codeResponse => {
      oauthSignIn(
        { provider: 'google', data: { authCode: codeResponse.code } },
        {
          onSuccess: () => {
            saveRememberedEmail();
            handleAuthSuccess();
          },
          onError: () => {
            setStep('EMAIL_INPUT');
            alertStore.open({
              title: t('Sign_In_01'),
              message: t('Sign_In_02'),
              useCancel: false,
            });
          },
        },
      );
    },
    onError: () => {
      setStep('EMAIL_INPUT');
    },
    flow: 'auth-code',
    hint: email.trim() || undefined,
  });

  const handleGoogleLogin = () => {
    googleLogin();
  };

  // --- Microsoft OAuth (redirect 방식) ---

  const handleMicrosoftLogin = () => {
    const trimmedEmail = email.trim();
    const redirectUri = `${window.location.origin}/login`;

    sessionStorage.set('oauth_provider', 'microsoft');
    if (trimmedEmail) {
      sessionStorage.set('sso_login_email', trimmedEmail);
    }
    sessionStorage.set('sso_remember_email', String(rememberEmail));
    // 매니저 페이지에서 호출된 경우: OAuth 콜백이 /login에 도달해도 매니저 컨텍스트 복원
    // 그 외(employee)에서 호출된 경우: 이전에 저장된 매니저 컨텍스트 제거
    if (isItAdminPage && tenantId) {
      sessionStorage.set('oauth_is_admin_page', 'true');
      sessionStorage.set('oauth_manager_tenant_id', tenantId);
    } else {
      sessionStorage.removeItem('oauth_is_admin_page');
      sessionStorage.removeItem('oauth_manager_tenant_id');
    }

    const queryParams: Record<string, string> = {
      response_type: 'code',
      client_id: MICROSOFT_CLIENT_ID,
      redirect_uri: redirectUri,
      scope: 'https://graph.microsoft.com/User.Read openid email profile',
    };

    // 이메일이 입력된 경우 login_hint로 계정 선택 창 건너뜀, 미입력 시 계정 선택 창 표시
    if (trimmedEmail) {
      queryParams.login_hint = trimmedEmail;
    } else {
      queryParams.prompt = 'select_account';
    }

    const url = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${qs.stringify(queryParams)}`;
    window.location.href = url;
  };

  // --- Effects ---

  // routeAfterLogin 보존
  useLayoutEffect(() => {
    if (location.state?.routeAfterLogin) {
      saveRouteAfterLogin(location.state.routeAfterLogin);
    }
  }, [location.state]);

  // 인증 에러 5초 후 자동 해제
  useEffect(() => {
    const timerId = setTimeout(() => {
      setIsAuthenticationFailed(false);
      setLoginErrorMessage(null);
      setInvalidTenant(false);
    }, 5000);
    return () => clearTimeout(timerId);
  }, [isAuthenticationFailed]);

  // OAuth 콜백 처리 (Microsoft, SSO)
  useEffect(() => {
    const rawProvider = sessionStorage.get('oauth_provider');
    const provider = typeof rawProvider === 'string' ? rawProvider : null;
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthCode = urlParams.has('code');

    if (!provider && !hasAuthCode) return;

    if (provider === 'sso') {
      setStep('OAUTH_PROCESSING');
      handleSsoCallback(handleAuthSuccess, () => {
        setStep('EMAIL_INPUT');
      });
      sessionStorage.removeItem('oauth_provider');
      sessionStorage.removeItem('sso_registration_id');
      sessionStorage.removeItem('sso_tenant_id');
    } else if (provider === 'microsoft' && hasAuthCode) {
      setStep('OAUTH_PROCESSING');
      handleOAuthCallback('microsoft', handleAuthSuccess, () => {
        setStep('EMAIL_INPUT');
      });
      sessionStorage.removeItem('oauth_provider');
      sessionStorage.removeItem('sso_remember_email');
    } else {
      sessionStorage.removeItem('oauth_provider');
    }
  }, [handleOAuthCallback, handleSsoCallback, handleAuthSuccess]);

  return {
    // 상태
    step,
    email,
    ssoProviders,
    isAuthenticationFailed,
    loginErrorMessage,
    invalidTenant,
    isLoginPending,
    isLookupPending,
    isRedirecting,
    // MFA 상태
    mfaStep,
    mfaQrCodeUrl,
    mfaSecret,
    mfaError,
    mfaLoading,
    // 환경변수 파생
    showGoogleLogin,
    showMicrosoftLogin,
    disablePasswordLogin,
    showSsoAsDefault,
    // 아이디 기억하기
    rememberEmail,
    setRememberEmail,
    // 핸들러
    setEmail,
    handleEmailContinue,
    handlePasswordLogin,
    handleSsoProviderSelect,
    handleMfaVerify,
    handleChangeMfaStep,
    handleBackToEmail,
    handleGoogleLogin,
    handleMicrosoftLogin,
  };
};
